import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Truck, Wrench, Toolbox, DotsThree, CaretLeft, CaretRight, CheckCircle, FloppyDisk, Plus, Trash } from "@phosphor-icons/react";
import { ANSWER_TYPES, SEVERITIES, sevBadgeCls, typeLabel } from "../lib/checklist";

const KIND_ICON = { vehicle: Truck, trailer: Truck, plant: Wrench, tool: Toolbox, other: DotsThree };

const blankExtra = () => ({ prompt: "", answer_type: "pass_fail", is_mandatory: true, fail_severity: "", unit: "" });

export default function ChecklistWizard({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [opts, setOpts] = useState({ asset_kinds: [], check_types: [] });
  const [kind, setKind] = useState("");
  const [checkType, setCheckType] = useState("");
  const [freq, setFreq] = useState(null);
  const [name, setName] = useState("");
  const [validity, setValidity] = useState(24);
  const [library, setLibrary] = useState([]);
  const [picked, setPicked] = useState({}); // libId -> true
  const [extras, setExtras] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1); setKind(""); setCheckType(""); setFreq(null); setName(""); setValidity(24); setPicked({}); setExtras([]);
    api.get("/checks/wizard-options").then((r) => setOpts(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!kind || kind === "other") { setLibrary([]); setPicked({}); return; }
    api.get(`/checks/library?asset_kind=${kind}`).then((r) => {
      setLibrary(r.data);
      const all = {}; r.data.forEach((q) => { all[q.id] = true; });
      setPicked(all);
    }).catch(() => {});
  }, [kind]);

  const kindLabel = (opts.asset_kinds.find((k) => k.value === kind) || {}).label || "";
  const typeLbl = (opts.check_types.find((t) => t.value === checkType) || {}).label || "";

  const chooseType = (t) => {
    setCheckType(t.value); setFreq(t.frequency_days);
    if (!name && kindLabel) setName(`${kindLabel} ${t.label.split(" / ")[0]} Check`);
  };

  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));
  const setExtra = (i, k, v) => setExtras((arr) => arr.map((e, j) => (j === i ? { ...e, [k]: v } : e)));
  const addExtra = () => setExtras((a) => [...a, blankExtra()]);
  const delExtra = (i) => setExtras((a) => a.filter((_, j) => j !== i));

  const compiledQuestions = () => {
    const fromLib = library.filter((q) => picked[q.id]).map((q) => ({
      prompt: q.prompt, answer_type: q.answer_type, section: q.category,
      is_critical: q.is_critical, fail_severity: q.fail_severity, creates_fault: q.creates_fault,
      requires_photo: q.requires_photo, unit: q.unit,
    }));
    const fromExtra = extras.filter((e) => e.prompt.trim()).map((e) => ({
      prompt: e.prompt.trim(), answer_type: e.answer_type, is_mandatory: e.is_mandatory,
      fail_severity: e.fail_severity || null, unit: e.unit || null,
    }));
    return [...fromLib, ...fromExtra];
  };

  const finish = async (publish) => {
    setSaving(true);
    try {
      const payload = { name: name.trim(), asset_kind: kind || null, check_type: checkType || null,
        frequency_days: freq, validity_hours: Number(validity) || 24, questions: compiledQuestions() };
      const r = await api.post("/checks/templates", payload);
      if (publish) await api.post(`/checks/templates/${r.data.id}/publish`);
      toast.success(publish ? "Checklist published" : "Draft saved");
      onCreated(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const canNext = step === 1 ? !!kind : step === 2 ? (!!checkType && !!name.trim()) : true;
  const total = compiledQuestions().length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="checklist-wizard" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">Create Checklist
            <span className="ml-2 text-xs font-mono text-slate-400">Step {step} of 5</span></DialogTitle>
        </DialogHeader>

        {/* Step 1 — asset kind */}
        {step === 1 && (
          <div className="flex flex-col gap-3">
            <p className="font-body text-sm font-semibold text-slate-700">What is this check for?</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {opts.asset_kinds.map((k) => {
                const Icon = KIND_ICON[k.value] || DotsThree;
                return (
                  <button key={k.value} data-testid={`wiz-kind-${k.value}`} onClick={() => setKind(k.value)}
                    className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors duration-100 ${kind === k.value ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <Icon size={28} weight="bold" className={kind === k.value ? "text-blue-600" : "text-slate-500"} />
                    <span className="font-body text-sm font-semibold text-slate-800">{k.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2 — check type */}
        {step === 2 && (
          <div className="flex flex-col gap-3">
            <p className="font-body text-sm font-semibold text-slate-700">Choose the type of check</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {opts.check_types.map((t) => (
                <button key={t.value} data-testid={`wiz-type-${t.value}`} onClick={() => chooseType(t)}
                  className={`rounded-lg border-2 p-3 text-left transition-colors duration-100 ${checkType === t.value ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="font-body text-sm font-semibold text-slate-800">{t.label}</div>
                  {t.frequency_days && <div className="font-mono text-xs text-slate-400">every {t.frequency_days}d</div>}
                </button>
              ))}
            </div>
            <label className="text-xs font-semibold text-slate-600 mt-2">Checklist name</label>
            <Input data-testid="wiz-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Vehicle Daily Check" />
            <label className="text-xs font-semibold text-slate-600">Validity (hours a completed check stays valid)</label>
            <Input data-testid="wiz-validity" type="number" value={validity} onChange={(e) => setValidity(e.target.value)} className="w-32" />
          </div>
        )}

        {/* Step 3 — standard questions */}
        {step === 3 && (
          <div className="flex flex-col gap-2">
            <p className="font-body text-sm font-semibold text-slate-700">Standard questions {kind === "other" ? "" : `for ${kindLabel}`}</p>
            <p className="font-body text-xs text-slate-500">Tick the ones you want. You can add your own on the next step.</p>
            {library.length === 0 && <p className="text-sm text-slate-400 py-3">No standard questions for this type — add your own next.</p>}
            <div className="flex flex-col gap-1 max-h-[46vh] overflow-y-auto">
              {library.map((q) => (
                <label key={q.id} data-testid={`wiz-lib-${q.id}`} className={`flex items-start gap-2 rounded border-2 p-2 cursor-pointer ${picked[q.id] ? "border-blue-300 bg-blue-50/40" : "border-slate-200"}`}>
                  <input type="checkbox" checked={!!picked[q.id]} onChange={() => toggle(q.id)} className="mt-1" />
                  <span className="flex-1">
                    <span className="font-body text-sm text-slate-800">{q.prompt}</span>
                    <span className="ml-2 text-[10px] font-mono text-slate-400">{typeLabel(q.answer_type)}</span>
                    {q.fail_severity && <span className={`ml-1 text-[9px] font-bold uppercase px-1 rounded ${sevBadgeCls(q.fail_severity)}`}>{q.fail_severity}</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Step 4 — add extras */}
        {step === 4 && (
          <div className="flex flex-col gap-3">
            <p className="font-body text-sm font-semibold text-slate-700">Add anything else</p>
            {extras.map((e, i) => (
              <div key={i} data-testid={`wiz-extra-${i}`} className="rounded-lg border-2 border-slate-200 p-3 flex flex-col gap-2">
                <Input data-testid={`wiz-extra-prompt-${i}`} placeholder="Question text" value={e.prompt} onChange={(ev) => setExtra(i, "prompt", ev.target.value)} />
                <div className="flex flex-wrap gap-2 items-center">
                  <select data-testid={`wiz-extra-type-${i}`} className="h-9 border-2 border-slate-300 rounded px-2 text-sm" value={e.answer_type} onChange={(ev) => setExtra(i, "answer_type", ev.target.value)}>
                    {ANSWER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <select data-testid={`wiz-extra-sev-${i}`} className="h-9 border-2 border-slate-300 rounded px-2 text-sm" value={e.fail_severity} onChange={(ev) => setExtra(i, "fail_severity", ev.target.value)}>
                    {SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={e.is_mandatory} onChange={(ev) => setExtra(i, "is_mandatory", ev.target.checked)} />Required</label>
                  <button data-testid={`wiz-extra-del-${i}`} onClick={() => delExtra(i)} className="ml-auto text-red-500"><Trash size={16} weight="bold" /></button>
                </div>
                {e.fail_severity && <p className="text-[11px] text-slate-500">A failure will raise a <b>{e.fail_severity}</b> fault.</p>}
              </div>
            ))}
            <Button data-testid="wiz-add-extra" variant="outline" onClick={addExtra} className="font-semibold self-start"><Plus size={16} weight="bold" className="mr-1" />Add Question</Button>
          </div>
        )}

        {/* Step 5 — review */}
        {step === 5 && (
          <div className="flex flex-col gap-3">
            <p className="font-body text-sm font-semibold text-slate-700">Review &amp; Publish</p>
            <div className="rounded-lg border-2 border-slate-200 p-3">
              <div className="font-display text-lg font-black text-slate-900">{name || "Untitled checklist"}</div>
              <div className="font-mono text-xs text-slate-500">{kindLabel || "—"} · {typeLbl || "—"} · {total} question{total === 1 ? "" : "s"}</div>
            </div>
            <div className="flex flex-col gap-1 max-h-[42vh] overflow-y-auto" data-testid="wiz-review-list">
              {compiledQuestions().map((q, i) => (
                <div key={i} className="flex items-center gap-2 rounded border border-slate-200 px-2 py-1.5">
                  <span className="font-mono text-xs text-slate-400">{i + 1}</span>
                  <span className="flex-1 font-body text-sm text-slate-800">{q.prompt}</span>
                  <span className="text-[10px] font-mono text-slate-400">{typeLabel(q.answer_type)}</span>
                  {q.fail_severity && <span className={`text-[9px] font-bold uppercase px-1 rounded ${sevBadgeCls(q.fail_severity)}`}>{q.fail_severity}</span>}
                </div>
              ))}
              {total === 0 && <p className="text-sm text-slate-400 py-2">No questions yet — go back and add some.</p>}
            </div>
          </div>
        )}

        {/* Nav */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-200 pt-3 mt-1">
          <Button data-testid="wiz-back" variant="outline" disabled={step === 1} onClick={() => setStep((s) => s - 1)} className="font-semibold"><CaretLeft size={16} weight="bold" className="mr-1" />Back</Button>
          {step < 5 ? (
            <Button data-testid="wiz-next" disabled={!canNext} onClick={() => setStep((s) => s + 1)} className="bg-slate-900 font-semibold">Next<CaretRight size={16} weight="bold" className="ml-1" /></Button>
          ) : (
            <div className="flex gap-2">
              <Button data-testid="wiz-save-draft" variant="outline" disabled={saving || total === 0} onClick={() => finish(false)} className="font-semibold"><FloppyDisk size={16} weight="bold" className="mr-1" />Save Draft</Button>
              <Button data-testid="wiz-publish" disabled={saving || total === 0} onClick={() => finish(true)} className="bg-emerald-600 hover:bg-emerald-700 font-semibold"><CheckCircle size={16} weight="bold" className="mr-1" />Publish</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
